import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <header className="header">
        <div className="container">
          <div className="header-left">
            <Link to="/" className="logo">
              <img src="/assets/ic_kidstrail.png" alt="KidsTrail" className="logo-icon" />
              KidsTrail
            </Link>
          </div>
          <div className="header-center">
            <div className="header-download-buttons">
              <a
                href="https://apps.apple.com/app/id6749541872"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_applestore.png" alt="Download on App Store" className="header-store-icon" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kidstrail.app"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="header-store-icon" />
              </a>
            </div>
            <div className="header-social-buttons">
              <a
                href="https://www.youtube.com/@KidsTrailCare"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img src="/assets/ic_youtube.png" alt="YouTube" className="header-social-icon" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61585649347570"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src="/assets/ic_facebook.png" alt="Facebook" className="header-social-icon" />
              </a>
              <a
                href="https://www.instagram.com/kidstrail.care/"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src="/assets/ic_instagram.png" alt="Instagram" className="header-social-icon" />
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <div className="policy-content">
            <h1>Privacy Policy</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="policy-section">
              <h2>Introduction</h2>
              <p>
                Welcome to KidsTrail! We are committed to protecting the privacy and safety of children 
                who use our application. This Privacy Policy explains how we collect, use, and protect 
                information when you use our KidsTrail mobile application.
              </p>
            </section>

            <section className="policy-section">
              <h2>Information We Collect</h2>
              <h3>Personal Information</h3>
              <ul>
                <li>We do not collect any personal information from children</li>
                <li>Parents may sign in with Apple or Google. When they do, we receive an email address and a unique account identifier from those providers, which we use only for account access, subscription management, and service communication.</li>
                <li>All user-created content is saved locally on the device. Users can back up their content manually with an appropriate subscription.</li>
              </ul>

              <h3>Non-Personal Information</h3>
              <ul>
                <li>Aggregated, non-personal app usage events (for example, which screens are opened) to improve user experience</li>
                <li>Generic device information such as operating system version, language, and device model for technical support purposes</li>
                <li>Crash reports to maintain app stability</li>
                <li>Aggregated, non-personalised ad-serving data for free-tier users (see "Subscription and Advertising" below)</li>
              </ul>

              <h3>Identifiers We Do NOT Use</h3>
              <ul>
                <li>We do <strong>not</strong> use Apple's Advertising Identifier (IDFA)</li>
                <li>We do <strong>not</strong> use Google's Advertising ID (AAID)</li>
                <li>We do <strong>not</strong> show Apple's App Tracking Transparency prompt</li>
                <li>We do <strong>not</strong> build behavioural profiles of any user, especially children</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>How We Use Information</h2>
              <ul>
                <li>To provide and maintain our service</li>
                <li>To improve app functionality and user experience</li>
                <li>To provide customer support</li>
                <li>To ensure app security and prevent misuse</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Information Sharing and Disclosure</h2>
              <div className="highlight-box">
                <h3>🔒 Our Commitment to Privacy</h3>
                <ul>
                  <li><strong>We do not sell, rent, or trade any user information</strong></li>
                  <li><strong>We do not share user content with other users</strong></li>
                  <li><strong>We do not use any data for cross-app or cross-site tracking</strong></li>
                  <li><strong>We do not build advertising profiles of any user</strong></li>
                </ul>
                <p>
                  We use a small number of trusted third-party service providers
                  to operate the app. Each provider receives only the minimum
                  data needed for their specific function, and none of them are
                  permitted to use that data for tracking or behavioural
                  advertising. See "Third-Party Services" below for the full
                  list.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>Third-Party Services</h2>
              <p>
                KidsTrail relies on the following third-party services. Each is
                configured for child-safe, non-tracking use:
              </p>
              <ul>
                <li>
                  <strong>Firebase Authentication (Google)</strong> — handles
                  parent sign-in (Apple ID / Google account) and account
                  recovery.
                </li>
                <li>
                  <strong>Firebase Analytics (Google)</strong> — aggregated,
                  non-personalised in-app event counts. No IDFA, no advertising
                  ID, no cross-app tracking.
                </li>
                <li>
                  <strong>Firebase Crashlytics (Google)</strong> — anonymous
                  crash reports so we can fix bugs.
                </li>
                <li>
                  <strong>Firebase Performance Monitoring (Google)</strong> —
                  aggregated performance metrics (launch time, network
                  latency).
                </li>
                <li>
                  <strong>Firebase Cloud Storage (Google)</strong> — used only
                  by subscribed users who choose to back up their own content;
                  files are stored under the parent's account.
                </li>
                <li>
                  <strong>RevenueCat</strong> — manages subscription state
                  (active, expired, renewal). Receives only the subscription
                  transaction and an anonymous user identifier.
                </li>
                <li>
                  <strong>Google AdMob</strong> — serves non-personalised,
                  child-directed advertisements <em>only</em> to users on the
                  free, ad-supported tier (see "Subscription and Advertising"
                  below). Configured with <code>TagForChildDirectedTreatment</code>,
                  <code>TagForUnderAgeOfConsent</code>, and a maximum ad
                  content rating of <strong>G (General audiences)</strong>.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Parental Control Features</h2>
              <ul>
                <li>Parents have full control over their child's account</li>
                <li>Parents can review and delete any content created by their child</li>
                <li>Parents can set usage limits and restrictions</li>
                <li>Parents can disable certain features as needed</li>
                <li>All account settings require parental authentication</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Subscription and Advertising</h2>
              <p>
                After a free trial period, parents are asked to choose how
                they would like to continue using KidsTrail:
              </p>
              <ul>
                <li>
                  <strong>Subscribe</strong> — an ad-free experience. No
                  advertisements are shown to subscribed users at any time.
                </li>
                <li>
                  <strong>Continue free with ads</strong> — KidsTrail remains
                  free to use, with small banner advertisements served by
                  Google AdMob.
                </li>
              </ul>
              <h3>How advertising is kept child-safe</h3>
              <ul>
                <li>
                  All advertisements are <strong>non-personalised</strong> —
                  they are not based on the user's browsing history, identity,
                  or behaviour.
                </li>
                <li>
                  Every ad request is tagged as <strong>child-directed under
                  COPPA</strong> and <strong>under the age of consent under
                  GDPR-K</strong>.
                </li>
                <li>
                  Maximum ad content rating is restricted to <strong>G (General
                  audiences)</strong>; ads rated PG, T, or MA are blocked at
                  the network level.
                </li>
                <li>
                  No advertising identifiers (Apple's IDFA or Google's
                  Advertising ID) are collected or transmitted.
                </li>
                <li>
                  Google may collect aggregated, non-personalised data to
                  serve and measure these advertisements — see Google's{' '}
                  <a
                    href="https://policies.google.com/technologies/ads"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Advertising privacy policy
                  </a>
                  .
                </li>
                <li>
                  Parents can remove ads at any time by subscribing from the
                  Settings or the in-app subscription page.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Data Security</h2>
              <ul>
                <li>All data is encrypted during transmission</li>
                <li>User content is stored securely</li>
                <li>We implement industry-standard security measures</li>
                <li>Regular security audits are conducted</li>
                <li>Access to any data is strictly limited and monitored</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Children's Privacy Protection</h2>
              <p>
                KidsTrail is designed with children's privacy as our top priority. We comply with 
                children's privacy laws including COPPA (Children's Online Privacy Protection Act). 
                We do not knowingly collect personal information from children under 13 without 
                parental consent.
              </p>
            </section>

            <section className="policy-section">
              <h2>Data Retention</h2>
              <ul>
                <li>User-created content can be deleted at any time</li>
                <li>When a premium subscription ends, uploaded content is retained for 30 plus days before automatic deletion (unless user requests immediate deletion)</li>
                <li>When an account is closed, account information is deleted and all other data is archived immediately, making it inaccessible. The archived data is permanently deleted within a maximum of 30 days. Users can also choose to delete their data and account immediately from within the app, which provides a faster deletion process.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Your Rights</h2>
              <p>Parents and guardians have the right to:</p>
              <ul>
                <li>Review any information collected about their child</li>
                <li>Request deletion of their child's information</li>
                <li>Refuse further collection of their child's information</li>
                <li>Contact us with any privacy-related questions or concerns</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify parents of any 
                material changes by posting the new Privacy Policy on this page and updating the 
                "last updated" date.
              </p>
            </section>

            <section className="policy-section">
              <h2>Content Responsibility and Parental Guidance</h2>
              <div className="highlight-box">
                <h3>⚠️ Important Notice for Parents</h3>
                <p>
                  We have tried our best to provide proper and suitable content for children. However, 
                  parents are encouraged and responsible to check the app contents regularly and let us 
                  know if there are any issues with the content or anything inappropriate.
                </p>
                <p>
                  <strong>If parents find any content inappropriate:</strong>
                </p>
                <ul>
                  <li>They should contact us immediately at care@kidstrail.net</li>
                  <li>We will review the content and take appropriate actions</li>
                  <li>If parents believe the app is not suitable, they should uninstall it immediately</li>
                  <li>For subscribers who have concerns about content appropriateness, we may review the content and provide a refund if possible</li>
                </ul>
                <p>
                  <strong>Parental supervision is always recommended</strong> when children use any digital content, 
                  including KidsTrail.
                </p>
              </div>
            </section>

            <section className="policy-section">
              <h2>Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices, 
                please contact us at:
              </p>
              <div className="contact-info">
                <p>Email: care@kidstrail.net</p>
                <p>Content Issues: care@kidstrail.net</p>
                <p>Subject: Privacy Policy Inquiry</p>
              </div>
            </section>

            <div className="policy-footer">
              <p>
                <strong>Remember:</strong> KidsTrail is a trusted, child-safe
                app with strong parental controls. Ads — when shown to free-tier
                users — are non-personalised and rated G. Your child's privacy
                and safety are our highest priorities.
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 KidsTrail. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/" className="footer-link">Home</Link>
            <Link to="/contact-us" className="footer-link">Contact Us</Link>
            <Link to="/terms-of-use" className="footer-link">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
